/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type {
  Station,
  Suggestions,
  UsestationListDataParams,
  StationListModel,
  stationListModel,
} from '../types';
import { useMemo } from 'react';
import { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import {
  getColor,
  pd,
  textStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import type { SectionListItem } from '@airasia-phoenix/react-native/components/SearchableListPageView';
import { LabelType } from '@airasia-phoenix/react-native/components/LabelView/types';
import {
  BASE_PADDING_START,
  CHILD_PADDING_OFFSET,
  DEFAULT_STATIONTYPE,
  TRAVEL_TYPE,
  HOTEL_TYPE,
  ALLOWED_TYPES,
  MIN_QUERY_LENGTH,
  RECENT_HS_LIST,
  GENERAL,
} from '../configs/constants';
import type { HotelSection, HotelDestinationListItem } from '../types';
// import { buildstationSearchItems } from '@airasia-rn/snap';

type TranslationFn = (key: string) => string;

const getLocationFromFullName = (name: string, fullName: string): string => {
  if (!fullName) return '';
  const prefix = `${name},`;
  if (fullName.startsWith(prefix)) {
    return fullName.slice(prefix.length).trim();
  }
  return fullName;
};

export const getstationDisplayValue = (
  station: Station | null,
  isAllAirports: boolean,
  t: TranslationFn,
) => {
  if (!station) return '';
  if (isAllAirports) {
    return `${station.CityName} (${t('searchSnapPage.stationField.allAirports')})`;
  }
  return `${station.CityName} (${station.StationCode})`;
};

const getHighlightSpans = (text: string, query: string) => {
  if (!query) return undefined;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return undefined;

  return [
    {
      spannable: text.substring(index, index + query.length),
      isDisabled: true,
      style: {
        ...textStyle(
          pd.sys.typescale.body_text_medium,
          pd.sys.colour.content_primary,
        ),
        fontWeight: 'bold' as const,
        textDecorationLine: 'none' as const,
      },
    },
  ];
};

export const buildRecentItems = (
  recentStations: Array<{ station: Suggestions; isAllAirports: boolean }>,
  t: TranslationFn,
  query: string = '',
): MemoizedListViewProps[] => {
  return recentStations?.map((item, index) => {
    const primaryText = item?.station?.name ?? '';
    const secondaryText = getLocationFromFullName(
      primaryText,
      item?.station?.full_name ?? '',
    );
    return {
      id: `recent-${item?.station?.id}-${index}`,
      model: {
        station: item?.station,
        isAllAirports: false,
        trackingSource: RECENT_HS_LIST,
        trackingIndex: index + 1,
      },
      primaryText,
      primaryHighlightText: getHighlightSpans(primaryText, query),
      secondaryText,
      startElement: {
        iconViewProps: {
          icon: pd.icon.main.actions.history,
          height: 24,
          width: 24,
          tint: getColor(pd.sys.colour.content_primary),
        },
      },
      endElement: {
        labelViewProps: {
          text: item?.station?.type
            ? item.station.type.charAt(0).toUpperCase() +
              item.station.type.slice(1)
            : '',
          type: LabelType.TONED_BLUE,
        },
      },
      paddingStart: BASE_PADDING_START,
    };
  });
};

export const buildPlaceSearchItems = (
  items: Array<{
    aa_id: string;
    city_id: string | null;
    coordinates: { lat: number; lon: number };
    country_code: string;
    full_name: string;
    id: string;
    name: string;
    type: string; // "hotel" | "airport"
  }>,
  query: string,
  t: TranslationFn,
): MemoizedListViewProps[] => {
  const listItems: MemoizedListViewProps[] = [];
  const usedIds = new Set<string>();

  items.forEach((item, index) => {
    const primaryText = item.name;
    const secondaryText = getLocationFromFullName(item.name, item.full_name);
    const baseId = `${item.type}-${item.id}`;
    let id = baseId;
    if (usedIds.has(id)) {
      id = `${baseId}-${item.aa_id || index}`;
    }
    if (usedIds.has(id)) {
      id = `${baseId}-${item.aa_id || 'item'}-${index}`;
    }
    usedIds.add(id);

    listItems.push({
      id,
      model: {
        place: item,
        isAllAirports: false,
        trackingSource: GENERAL,
        trackingIndex: index + 1,
      },
      primaryText,
      primaryHighlightText: getHighlightSpans(primaryText, query),
      secondaryText,
      startElement: {
        iconViewProps: {
          icon:
            item?.type === HOTEL_TYPE
              ? pd.icon.main.maps.hotel
              : item?.type === TRAVEL_TYPE
                ? pd.icon.main.device.airplanemode_active
                : pd.icon.main.communication.location_on,
          height: 24,
          width: 24,
          tint: getColor(pd.sys.colour.content_primary),
        },
      },
      endElement: {
        labelViewProps: {
          text: item.type
            ? item.type.charAt(0).toUpperCase() + item.type.slice(1)
            : '',
          type: LabelType.TONED_BLUE,
        },
      },
      paddingStart: BASE_PADDING_START,
    });
  });

  return listItems;
};

export const useStationListData = ({
  query,
  stations,
  recentstations,
  t,
}: UsestationListDataParams): SectionListItem[] | MemoizedListViewProps[] => {
  const recentItems = useMemo(
    () => buildRecentItems(recentstations, t, query),
    [recentstations, t, query],
  );

  return useMemo(() => {
    const trimmedQuery = query?.trim() || '';
    if (trimmedQuery.length > 0) {
      if (trimmedQuery.length >= MIN_QUERY_LENGTH) {
        const filteredStations = stations?.filter((station) =>
          ALLOWED_TYPES.includes(station?.type),
        );

        return buildPlaceSearchItems(filteredStations, trimmedQuery, t);
      }
      return [];
    }

    if (recentItems?.length > 0) {
      return [
        {
          title: t('searchSnapPage.recentSearch.title'),
          data: recentItems,
        },
      ];
    }

    return [];
  }, [query, stations, recentItems, t]);
};

const normalizeStationItem = (item: any) => {
  const station = item?.model?.place ?? item?.model?.station;

  if (!station) {
    return item;
  }

  return {
    ...item,
    model: {
      ...item.model,
      station: {
        ...station,
        Lat: station.coordinates?.lat ?? station.Lat,
        Long: station.coordinates?.lon ?? station.Long,
        StationCode: station.id ?? station.StationCode,
        StationName: station.name ?? station.StationName,
        StationType: station.type ?? station.StationType ?? DEFAULT_STATIONTYPE,
      },
    },
  };
};

export const normalizeHotelSectionData = (
  sections: (HotelSection<HotelDestinationListItem> | MemoizedListViewProps)[],
): any[] => {
  return sections?.map((sectionOrItem: any) => {
    if (sectionOrItem?.data && Array.isArray(sectionOrItem.data)) {
      return {
        ...sectionOrItem,
        data: sectionOrItem.data.map((item: any) => normalizeStationItem(item)),
      };
    }
    return normalizeStationItem(sectionOrItem);
  });
};
